import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { RootState } from '@core/interfaces/root-state';
import router from '@app/router/index.APP_TARGET';
import { CenterSettingState } from '@core/interfaces/center-setting-state';
import { CenterSetting } from '@core/interfaces/center-setting';
import { AccessControlConfigInterface } from '@core/interfaces/access-control-config-interface';
import { bpcApi } from '@core/services/bpc-api.service';
import BPCApiRouteHelper from '@core/helpers/bpc-api-route.helper';
import ModulesService from '@core/services/modules.service';
import ModulesHelper from '@core/services/modules.helper';

const bpcRouterHelper = new BPCApiRouteHelper();

// Initial state
const state: CenterSettingState = {
  centerSettings: null,
  accessControlConfig: null,
  loading: false,
};

// Synchronous functions to get data from state (can be composed properties)
const getters: GetterTree<CenterSettingState, RootState> = {
  centerSettings(state): CenterSetting {
    return state.centerSettings
      ? state.centerSettings
      : JSON.parse(localStorage.getItem('center-settings'));
  },
  accessControlConfig(state): CenterSetting {
    return state.accessControlConfig
      ? state.accessControlConfig
      : JSON.parse(localStorage.getItem('access_control_config'));
  },

  isLoading(state): boolean {
    return !!state.loading;
  },

  displayContactColorInBookingCalendar(state): boolean {
    return state?.centerSettings?.source_color_to_display_booking_calendar === 'contact';
  },

  stripeActivated(state: CenterSettingState, getters): boolean {
    return (
      getters?.centerSettings?.stripe_activated || getters?.centerSettings?.bancontact_activated
    );
  },

  stripePublicKey(state: CenterSettingState, getters): string {
    return getters?.centerSettings?.stripe_public_key;
  },

  availableLanguages(state, getters) {
    return getters.centerSettings?.available_languages || [];
  },
  totalAvailableLanguages(state, getters) {
    return getters.centerSettings?.available_languages.length || 0;
  },
  defaultLanguage(state, getters) {
    return getters.centerSettings?.default_language;
  },
  allowAccessPublicWebsite(state, getters): boolean {
    return !!getters.centerSettings?.allow_access_public_website;
  },
  allowAccessClientSection(state, getters): boolean {
    return !!getters.centerSettings?.allow_access_client_section;
  },
  backgroundUrl(state, getters) {
    return getters?.centerSettings?.background?.url;
  },

  logoUrl(state, getters) {
    return getters?.centerSettings?.logo_url?.url;
  },

  posDefaultContactId(state, getters) {
    return getters?.centerSettings?.invoice_from_ticket_contact_id;
  },

  accountingIntegrationSettings(state, getters) {
    return getters?.centerSettings?.accounting_integration_settings;
  },
  shoppingCartActivated(state: CenterSettingState, getters): boolean {
    return !!getters?.centerSettings?.activate_shopping_cart;
  },
  advancedPrefixManagement(state, getters) {
    return getters?.centerSettings?.advanced_prefix_management;
  },
  analyticsActivated(state: CenterSettingState, getters): boolean {
    return !!getters?.centerSettings?.analytics_usage;
  },
  groupSubventionOnInvoices(state, getters) {
    return getters?.centerSettings?.group_subvention_on_invoices;
  },
  showActivityManagementPage(state, getters) {
    return getters?.centerSettings?.show_activity_management_page;
  },
  defaultSubsidizingContact(state, getters) {
    return getters?.centerSettings?.default_subsidizing_contact_id;
  },

  defaultPriceCategory(state, getters) {
    return getters?.centerSettings?.default_price_category;
  },

  defaultPriceCategoryBasedOnPostalCode(state, getters) {
    return getters?.centerSettings?.default_price_category_id_for_postal_codes;
  },

  postalCodesForDefaultPriceCategory(state, getters) {
    return getters?.centerSettings?.postal_codes_for_default_price_category;
  },

  centerType(state, getters) {
    return getters?.centerSettings?.center_type;
  },

  customContactFields(state, getters) {
    return getters?.centerSettings?.custom_contact_fields;
  },
  defaultInvoiceLayout(state, getters) {
    return getters?.centerSettings?.default_invoice_layout;
  },

  cancelledReservationsRemainDue(state, getters) {
    return getters?.centerSettings?.cancelled_unpaid_booking_remain_due;
  },

  allowBookingRefunds(state, getters) {
    return getters?.centerSettings?.allow_booking_refunds;
  },

  allowCoupon(state, getters) {
    return getters?.centerSettings?.allow_coupon;
  },
  moneyTransferConfirmationPageInfo(state, getters) {
    return getters?.centerSettings?.money_transfer_confirmation_page_info;
  },
  usePosV2(state, getters) {
    return getters?.centerSettings?.use_pos_v2;
  },
  useContactRelationsV2(state, getters) {
    return getters?.centerSettings?.use_contact_relations_v2;
  },
  enableContactCard(state, getters) {
    return getters?.centerSettings?.enable_contact_card;
  },
  useArticleImpacts(state, getters) {
    return getters?.centerSettings?.use_article_impacts;
  },
  useMultiplayerBookings(getters) {
    return getters?.centerSettings?.use_multiplayer_bookings;
  },
  countryCode(getters) {
    return getters?.centerSettings?.country;
  },
};

// Asynchronous functions to get data usually from an api call
const actions: ActionTree<CenterSettingState, RootState> = {
  // updateOptions({ commit }, payload: Option) {
  //   console.log('payload', payload);
  //   commit('setOptions', payload);
  // },
  updateCenterSettings({ commit }, payload: CenterSetting) {
    commit('updateCenterSettings', payload);
    router.go(null);
  },

  updateCenterSettingsNoRefresh({ commit }, payload: CenterSetting) {
    commit('updateCenterSettings', payload);
  },

  getAccessConfigAfterLogin({ commit }): Promise<boolean> {
    const $modulesService = new ModulesService();
    if ($modulesService.hasModuleActivated(ModulesHelper.ACCESS_CONTROL_MODULE_NAME)) {
      return new Promise((resolve, reject) => {
        bpcApi.get(bpcRouterHelper.getAccessControlConfigPath(), {}, 'admin').subscribe(
          res => {
            commit('updateAccessControlConfig', res.data);
            resolve(true);
          },
          error => {
            reject(error);
          },
        );
      });
    }
  },
};

// The vuex way of changing a state property
export const mutations: MutationTree<CenterSettingState> = {
  stopLoading(state) {
    state.loading = false;
  },
  startLoading(state) {
    state.loading = true;
  },

  updateCenterSettings(state, data: CenterSetting | any) {
    if (data.globalSetting) {
      data = {
        id: data.id,
        center_id: data.center_id,
        ...data.globalSetting,
        ...data.usersSetting,
        ...data.contactsSetting,
        ...data.subscriptionsSetting,
        ...data.bookingsSetting,
        ...data.activitiesSetting,
        ...data.accountingSetting,
        ...data.contractsSetting,
        ...data.pointOfSalesSetting,
        ...data.onlineShopSetting,
        ...data.websiteSetting,
        ...data.accessControlSetting,
        ...data.hiddenSetting,
      };
    }
    state.centerSettings = {
      ...state.centerSettings,
      ...data,
    };
    localStorage.setItem('center-settings', JSON.stringify(state.centerSettings));
    /* store.commit('auth/setUserLang', `${data.default_language}`); */
  },
  updateAccessControlConfig(state, data: AccessControlConfigInterface) {
    state.accessControlConfig = data;
    localStorage.setItem('access_control_config', JSON.stringify(data));
  },

  clearCenterSettings(state) {
    state.centerSettings = null;
    localStorage.removeItem('center-settings');
  },
};

export const CenterSettingStoreModule: Module<CenterSettingState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
